import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Button, Menu, MenuItem, Stack } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import { getCanvasAnnotationsFromROIs } from "features/canvas/utils";
import { Typography } from "new-components/typography/Typography";
import { CanvasAnnotation } from "types/imageAnnotations";
import { ImageQuery } from "types/imageQueries";
import { generateImageUrl } from "utils/generateImageUrl";
import { convertUTCtoLocalReadableDate } from "utils/utilityFunctions";

import { ImageCanvas } from "./ImageCanvas";

export interface ImageQueryThumbnailProps {
  setIsDeleteModalOpen: (imagequery: ImageQuery) => void;
  selected?: boolean;
  isQueryDetailsModal?: boolean;
  detectorData: ImageQuery;
}

function ImageQueryThumbnail(props: ImageQueryThumbnailProps) {
  const { setIsDeleteModalOpen, selected, isQueryDetailsModal, detectorData } = props;

  const detectorNameRef = useRef<HTMLParagraphElement>(null);
  const detectorQueryRef = useRef<HTMLParagraphElement>(null);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { predictor_name: detectorName, query, iq_id: iqID, created_at: timeStamp } = detectorData;

  const handleDropdownClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
  };
  const handleDropdownOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
  };

  const onDeleteImageQuery = (event: React.MouseEvent<HTMLElement>) => {
    setIsDeleteModalOpen(detectorData);
    handleDropdownClose(event);
    event.stopPropagation();
  };

  // annotations:
  const [annotations, setAnnotations] = useState<CanvasAnnotation[]>([]);
  const imageCanvasRef = useRef<React.ElementRef<typeof ImageCanvas>>(null);

  useEffect(() => {
    const rois = detectorData.current_best_prediction_rois
      ? getCanvasAnnotationsFromROIs(detectorData.current_best_prediction_rois)
      : null;
    if (rois) {
      setAnnotations(rois);
    }
  }, [detectorData, setAnnotations]);

  const handleDownloadImage = async (event: React.MouseEvent<HTMLElement>) => {
    if (imageCanvasRef.current) {
      await imageCanvasRef.current.downloadImage(`image_query-${iqID}.png`);
    }
    handleDropdownClose(event);
    event.stopPropagation();
  };

  return (
    <Stack
      direction="row"
      className="flex h-[70px] w-full cursor-pointer items-center group"
      sx={{ columnGap: 1 }}
    >
      <Box flexShrink={0} className="w-[93.33px] h-full" data-testid={`image-${iqID}`}>
        <ImageCanvas
          ref={imageCanvasRef}
          imageUrl={generateImageUrl(iqID)}
          annotations={annotations}
          style={{ cursor: "pointer" }}
          iqId={iqID}
        />
      </Box>
      <Stack flexGrow={1} overflow="hidden">
        {!isQueryDetailsModal && (
          <>
            <Typography
              ref={detectorNameRef}
              className="text-[#0D297D]"
              noWrap
            >
              {detectorName}
            </Typography>
            <Typography
              ref={detectorQueryRef}
              variant="detail"
              className="text-[#0D297D]"
              noWrap
            >
              {query}
            </Typography>
          </>
        )}
        {isQueryDetailsModal && (
          <>
            <Typography className="text-[#0D297D]">
              {convertUTCtoLocalReadableDate(timeStamp).split(",")[0]}
            </Typography>
            <Typography className="text-[#0D297D]">
              {convertUTCtoLocalReadableDate(timeStamp).split(",")[1]}
            </Typography>
          </>
        )}
      </Stack>
      <Button
        onClick={handleDropdownOpen}
        className={`self-start w-[40px] min-w-[40px] p-2 px-0
          ${selected ? "opacity-100" : "opacity-0"}
          group-hover:opacity-100 bg-inherit cursor-pointer`}
        sx={{
          display: {
            xs: "none",
            sm: "none",
            md: "block",
          }
        }}
      >
        <MoreVertIcon />
      </Button>
      <Menu
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleDropdownClose}
        MenuListProps={{ className: "p-0.5 px-1" }}
      >
        <MenuItem className="px-2 text-sm font-semibold" onClick={handleDownloadImage}>
          <FileDownloadIcon fontSize="small" className="mr-1" />
          Download Image
        </MenuItem>
        <MenuItem className="px-2 text-sm font-semibold" onClick={onDeleteImageQuery}>
          <DeleteIcon fontSize="small" className="mr-1" />
          Delete Image Query
        </MenuItem>
      </Menu>
    </Stack>
  );
}

export default ImageQueryThumbnail;
